import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Checkbox from '../../Form/Checkbox';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import AsyncCreatable from 'react-select/async-creatable';

import getAllPostTags from '../../../api/graphql/queries/getAllPostTags';
import getAllPostRoles from '../../../api/graphql/queries/getAllPostRoles';
import getPostsByCategory from '../../../api/graphql/queries/getPostsByCategory';
import postAddPost from '../../../api/graphql/mutations/postAddPost';
import postEditPost from '../../../api/graphql/mutations/postEditPost';

import imageModifier from '../../../utils/imageModifier';
import slugify from '../../../utils/slugify';
import { withIsoDates, withCreationDate } from '../../../utils/postDates';
import { replaceComponentKey } from '../../../utils/postComponents';
import { TITLE_LIMITS, DESCRIPTION_LIMITS, getPostSeoWarnings } from '../../../utils/seoFieldStatus';
import { getTourPriceWarnings } from '../../../utils/ticketPriceStatus';
import getTourShowsWithTickets from '../../../api/graphql/queries/getTourShowsWithTickets';

import LanguageTabsForms from '../../Tabs/LanguageTabsForms';
import DropDownMenu from '../../Form/DropDownMenu';
import DropDownSelector from '../../Form/DropDownSelector';
import Navigator from '../../Navigator';
import SingleObjUploaderWithSelector from '../Mam/SingleObjUploaderWithSelector';
import CharCounter from '../../Form/CharCounter';
import Modal from '../../Modal/Modal';
import checkPostSlugNameRepeated from '../../../utils/checkPostSlugNameRepeated';
import DateAndTimePickerPost from '../../Form/DateAndTimePickerPost';
import { usePostCategories, transformToSelectData, formatGroupLabel } from './configs/ReusableComponent';
import SpotifyArtistsPicker from './SpotifyArtistsPicker';

const placeholderButtonText = {
  en: 'BUY TICKETS',
  es: 'COMPRAR ENTRADAS',
  ca: 'COMPRAR ENTRADES',
  pt: 'COMPRAR BILHETES',
};

const height = { height: '1.2rem' };

const itemLayouts = [{ text: 'Normal', value: '0' }, { text: 'Newsletter', value: 'newsletter' }];

const headerKeys = ['header-main', 'header-main-v2', 'header-main-porto', 'header-main-pslife', 'header-main-pro-online'];
const FOOTER_KEYS = ['footer-main', 'footer-main-pslife', 'footer-main-pro-online'];

/*
The path the post will be served at. Leading slash included: the front appends
this value to the language segment to build the canonical and the hreflang
alternates, and a value stored without it produced URLs like /ennews/my-post.
*/
export const buildRelink = (postCategory, slugName) => {
  const section = (postCategory && postCategory.length && postCategory[0] !== 'blank') ? `/${postCategory[0]}` : '';

  return `${section}/${slugName}`;
};

const PostConfig = (props) => {
  const { post, layoutTemplate, isEdit, updatePost } = props;
  const { postCategory, postSubCategory } = useParams();
  const history = useHistory();
  const [postInfo, setPostInfo] = useState(layoutTemplate || post);
  const [isCTAButtonInactive, setIsCTAButtonInactive] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [status, setStatus] = useState({ status: 'creating', text: '', color: '' });
  const [isModalOn, setModalState] = useState(false);
  const [noIndex, setNoIndex] = useState(false);
  // TODO: change state management to a useReducer state
  const [requiresCompleteRegistration, setRequiresCompleteRegistration] = useState(false);
  const [activeLang, setActiveLanguage] = useState('');
  const [visibleLangImage, setSelectedImage] = useState({
    en: '',
  });
  const [currentRelatedAuthors, setCurrentRelatedAuthors] = useState(false);
  const [currentRelatedArtists, setCurrentRelatedArtists] = useState(false);
  const [currentRelatedMagazine, setCurrentRelatedMagazine] = useState(false);
  const [modalType, setModalType] = useState();

  const { categories, subcategories, setCurrentCategory } = usePostCategories(postCategory);
  const mainCategory = postInfo.postCategory?.[0] || 'blank';

  const isLoyaltyAchievement = postInfo.postCategory?.includes('loyalty-achievement');
  const isTourPost = postInfo.postCategory?.includes('tours');
  const isArtistPage = postInfo.postCategory?.includes('artist');
  const [tourPriceWarnings, setTourPriceWarnings] = useState([]);

  useEffect(() => {
    setCurrentCategory(mainCategory);
  }, [mainCategory]);

  /*
  Tours fichas feed Google an event per show, and a show can only carry a price
  when one of its visible tickets has one. Tickets sit two hops away (post ->
  events -> tickets), so this is fetched rather than derived from postInfo.
  Advisory: a failed fetch simply shows nothing.
  */
  useEffect(() => {
    let cancelled = false;
    if (!isTourPost || !postInfo.slugName) {
      setTourPriceWarnings([]);
      return undefined;
    }
    getTourShowsWithTickets(postInfo.slugName).then((shows) => {
      if (!cancelled) setTourPriceWarnings(getTourPriceWarnings(shows));
    });
    return () => { cancelled = true; };
  }, [isTourPost, postInfo.slugName]);


  const groupedOptions = [
    {
      label: 'Main Category',
      options: categories,
    },
    {
      label: 'Secondary Categories',
      options: subcategories || [],
    },
  ];

  useEffect(() => {
    if (!isEdit) return;
    const newPost = withIsoDates(post);
    const { components } = newPost;
    newPost.components = typeof components === 'string' ? JSON.parse(components) : components;

    setPostInfo(newPost);
  }, [post]);

  useEffect(() => {
    if (isEdit) return;

    const newPost = withCreationDate(postInfo);

    const postCategories = [postCategory];

    if (postSubCategory) postCategories.push(postSubCategory);

    newPost.postCategory = postCategories;

    setPostInfo(newPost);
  }, [post]);

  useEffect(() => {
    if (categories.length) setLoading(false);
  }, [categories]);

  const transformTags = (tags) => {
    if (!Array.isArray(tags)) {
      return [];
    }
    const returnedValues = [];
    if (tags.length > 0) {
      tags.map(i => returnedValues.push({ label: i, value: i }));
    }
    return returnedValues;
  };

  const transformTagsInverse = (tags) => {
    const returnedValues = [];
    tags.map(i => returnedValues.push(i.label));
    return returnedValues;
  };

  /* AsynSelect for Related Artists */
  const loadOptions = inputValue => new Promise((resolve) => {
    resolve(filterPostsByCategory('artista', inputValue));
  });

  const loadTags = inputValue => new Promise((resolve) => {
    resolve(filterAllTags(inputValue));
  });
  const loadRoles = inputValue => new Promise((resolve) => {
    resolve(filterAllRoles(inputValue));
  });

  const loadOptionsForRelatedAuthors = inputValue => new Promise((resolve) => {
    resolve(filterPostsByCategory('autor', inputValue));
  });

  const loadOptionsForRelatedMagazine = inputValue => new Promise((resolve) => {
    resolve(filterPostsByCategory('revista', inputValue));
  });

  const filterPostsByCategory = (category = 'artista', inputValue = '', filterLanguage = 'en') => getPostsByCategory(category, filterLanguage, inputValue)
    .then((res) => {
      if (res) {
        return res.posts;
      }
    })
    .catch(err => console.log(err, ' => getPostsByCategory'));

  const filterAllTags = (tagSearch = '') => {
    if (tagSearch === '') tagSearch = 'a';
    return getAllPostTags(tagSearch)
      .then((res) => {
        if (res) {
          return transformTags(res.tags);
        }
      })
      .catch(err => console.log(err, ' => getPostsByCategory'));
  };

  const filterAllRoles = (roleSearch = '') => {
    if (roleSearch === '') return; // roleSearch = 'a';
    return getAllPostRoles(roleSearch)
      .then((res) => {
        if (res) {
          return transformTags(res.roles);
        }
      })
      .catch(err => console.log(err, ' => getPostsByCategory'));
  };

  const handleInputChangeForRelatedAuthors = (changedValue) => {
    const elementToUpdate = [];

    changedValue.map((item) => {
      const {
        slugName: slug,
        postDescription,
      } = item;

      const { title: { en: name = '' } } = postDescription;

      elementToUpdate.push({
        name,
        slug,
      });
    });

    const { postDescription: currentPostDescription } = postInfo;

    postInfo.postDescription = { // this is mutating state
      ...currentPostDescription,
      authors: elementToUpdate,
    };

    setPostInfo({ ...postInfo });
  };

  const handleInputChangeForRelatedArtists = (changedValue) => {
    const elementToUpdate = [];

    changedValue.map((item) => {
      const {
        slugName: slug,
        postDescription,
      } = item;

      const { title: { en: name = '' } } = postDescription;

      elementToUpdate.push({
        name,
        slug,
      });
    });

    const { postDescription: currentPostDescription } = postInfo;

    postInfo.postDescription = { // this is mutating state
      ...currentPostDescription,
      relatedArtists: elementToUpdate,
    };

    setPostInfo({ ...postInfo });
  };

  const handleInputChangeForRelatedMagazine = (changedValue) => {
    let elementToUpdate = null;

    if (changedValue) {
      const {
        slugName,
        postDescription,
      } = changedValue;

      const { title: { en: title = '' } } = postDescription;

      elementToUpdate = {
        title,
        slugName,
      };
    }

    const { postDescription: currentPostDescription } = postInfo;

    postInfo.postDescription = { // this is mutating state
      ...currentPostDescription,
      relatedMagazine: elementToUpdate,
    };

    setPostInfo({ ...postInfo });
  };

  const handleInputChangeForOverlayImage = (value) => {
    const { postDescription: currentPostDescription } = postInfo; 

    postInfo.postDescription = { // this is mutating state
      ...currentPostDescription,
      overlayImage: value,
    };

    setPostInfo({ ...postInfo });
  };

  useEffect(() => {
    const { postDescription } = postInfo;

    const {
      authors: relatedAuthors = false,
      relatedArtists = false,
      relatedMagazine = false,
    } = postDescription;

    const valuesArtists = [];
    const valuesAuthors = [];
    let valueMagazine = false;
    if (Array.isArray(relatedAuthors)) {
      relatedAuthors.map((item) => {
        const { slug, name } = item;
        if (slug) {
          valuesAuthors.push({
            slugName: slug,
            postDescription: { title: { en: name } },
          });
        }
      });
      if (currentRelatedAuthors !== valuesAuthors) {
        setCurrentRelatedAuthors(valuesAuthors);
      }
    }
    if (Array.isArray(relatedArtists)) {
      relatedArtists.map((item) => {
        const { slug, name } = item;
        if (slug) {
          valuesArtists.push({
            slugName: slug,
            postDescription: { title: { en: name } },
          });
        }
      });
      if (currentRelatedArtists !== valuesArtists) {
        setCurrentRelatedArtists(valuesArtists);
      }
    }
    if (relatedMagazine) {
      const { slugName, title: name } = relatedMagazine;
      if (slugName) {
        valueMagazine = {
          slugName,
          postDescription: { title: { en: name } },
        };
      }
    }
    if (currentRelatedMagazine !== valueMagazine) {
      setCurrentRelatedMagazine(valueMagazine);
    }
  }, [postInfo]);
  /* END AsynSelect for Related Artists */

  // This is bad, use a better dropDown component
  const handleComponentKeyChange = (keyFragment, e) => {
    const components = replaceComponentKey(postInfo.components, keyFragment, e.toLowerCase());
    if (components === postInfo.components) return;

    setPostInfo({ ...postInfo, components });
  };

  const handleHeaderChange = e => handleComponentKeyChange('header', e);

  const handleFooterChange = e => handleComponentKeyChange('footer', e);

  const parsePostData = () => {
    const url = typeof postInfo.postDescription.url === 'string' ? postInfo.postDescription.url.trim() : '';
    let postDescUrl;
    if (url && url !== '/') {
      // logic to put a / at the begining of url in case it is not there and it is not https
      postDescUrl = url[0] === '/' || url.includes('http') ? url : `/${url}`;
    } else {
      // "/" used to be stored here, which made the page declare the language
      // homepage as its canonical URL. The post's own path is the only sane
      // fallback.
      postDescUrl = buildRelink(postInfo.postCategory, postInfo.slugName);
    }
    const postDescription = {
      ...postInfo.postDescription,
      url: postDescUrl,
      cta: {
        ...postInfo.postDescription.cta,
        text: {
          en: postInfo.postDescription?.cta?.text.en,
          es: postInfo.postDescription?.cta?.text.es,
          ca: postInfo.postDescription?.cta?.text.ca,
          pt: postInfo.postDescription?.cta?.text.pt,
        },
        url: postInfo.postDescription.cta?.url,
      },
    };
    const newPost = {
      ...postInfo,
      postDescription,
      components: String(JSON.stringify(postInfo.components)), // TODO: Not necessary to save components from Post Info updates. (david)
    };

    return newPost;
  };

  const addPost = async () => {
    if (!postInfo.slugName || postInfo.postCategory.length === 0) return;
    const slugNameRepeated = await checkPostSlugNameRepeated(postInfo.slugName);
    if (slugNameRepeated) {
      setStatus({ status: 'error', text: 'SlugName already taken', color: 'red' });
      return;
    }

    postAddPost(parsePostData())
      .then(({ isAdded, slugName }) => {
        if (!isAdded) {
          setStatus({ status: 'error', text: 'Post was not added', color: 'red' });
        } else {
          setStatus({ status: 'created' });
          history.push(`/cms/structure/${postInfo.postCategory[0] || 'blank'}/${slugName}`);
        }
      })
      .catch(err => console.log(err));
  };


  const editPost = () => {
    postEditPost(parsePostData())
      .then((res) => {
        const { isEdited = false } = res;
        if (isEdited) {
          props.addStatusMessage('icon-checkbox', 'Post updated', 'green');
          props.closeModal();
          // NOTE: update the current post state
          updatePost(postInfo);
        } else {
          setStatus({ status: 'error', text: 'Could not edit post, check that you have entered a valid slugName.', color: 'red' });
        }
      })
      .catch(err => console.log(err));
  };

  const toggleModal = (lang, imageType) => {
    setActiveLanguage(lang);
    setModalState(!isModalOn);
    setModalType(imageType);
  };


  const handleSelect = (option) => {
    const options = option.map(opt => opt.value);
    const postState = {
      ...postInfo,
      postCategory: options,
    };
    setPostInfo(postState);
  };


  const handleSlugNameInput = (e) => {
    const { value, name } = e.target;
    const slugName = slugify(value);
    const postDescription = {
      ...postInfo.postDescription,
      url: buildRelink(postInfo.postCategory, slugName),
    };

    const postState = {
      ...postInfo,
      [name]: slugName,
      postName: value,
      postDescription,
    };
    setPostInfo(postState);
  };

  const handlePostDescriptionLangInput = (e) => {
    const { value, lang, name } = e.target;
    const postDescription = {
      ...postInfo.postDescription,
      [name]: {
        ...postInfo.postDescription[name],
        [lang.toLowerCase()]: value,
      },
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
  };

  const handlePostDescriptionInput = (e) => {
    const { value, name, type, valueAsNumber } = e.target;

    const val = type === 'number' ? valueAsNumber : value;

    const postDescription = {
      ...postInfo.postDescription,
      [name]: val,
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
  };

  const handleCtaLangInput = (e) => {
    const { value, lang, name } = e.target;
    const cta = {
      ...postInfo.postDescription.cta,
      [name]: {
        ...postInfo.postDescription.cta[name],
        [lang.toLowerCase()]: value,
      },
    };
    const postState = {
      ...postInfo,
      postDescription: {
        ...postInfo.postDescription,
        cta,
      },
    };
    setPostInfo(postState);
  };

  const handleCtaUrlInput = (e) => {
    const { value, name } = e.target;
    const cta = {
      ...postInfo.postDescription.cta,
      [name]: value,
    };
    const postState = {
      ...postInfo,
      postDescription: {
        ...postInfo.postDescription,
        cta,
      },
    };
    setPostInfo(postState);
  };

  const handleCtaIsDisabledInput = (value) => {
    const cta = {
      ...postInfo.postDescription.cta,
      isDisabled: value,
    };
    const postState = {
      ...postInfo,
      postDescription: {
        ...postInfo.postDescription,
        cta,
      },
    };
    setIsCTAButtonInactive(value);
    setPostInfo(postState);
  };

  const handleDate = (name, date) => {
    const formattedDate = date ? new Date(date).toString() : null;
    const postDescription = {
      ...postInfo.postDescription,
      [name]: formattedDate,
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
  };

  const resetDate = (name) => {
    const postDescription = {
      ...postInfo.postDescription,
      [name]: null,
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
  };

  const addNewTag = (name) => {
    const { postDescription: { tags } } = postInfo;
    toggleTag([...transformTags(tags), { label: name, value: name }]);
  };

  const addNewRole = (name) => {
    const { rolesAllowed = [] } = postInfo;
    toggleRole([...transformTags(rolesAllowed), { label: name, value: name }]);
  };

  const toggleRole = (name) => {
    const nameArray = transformTagsInverse(name);
    const postState = {
      ...postInfo,
      rolesAllowed: nameArray,
    };
    setPostInfo(postState);
  };

  const toggleTag = (name) => {
    const nameArray = transformTagsInverse(name);
    const postDescription = {
      ...postInfo.postDescription,
      tags: nameArray,
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
  };

  const setImage = ([obj]) => {
    const newImage = {
      ...visibleLangImage,
      [activeLang]: obj.objectUrl,
    };
    setSelectedImage(newImage);
    const postDescription = {
      ...postInfo.postDescription,
      image: {
        ...postInfo.postDescription.image,
        [activeLang.toLowerCase()]: obj.url,
      },
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
    toggleModal();
  };

  const setImageSocial = ([obj]) => {
    const postDescription = {
      ...postInfo.postDescription,
      imageSocial: {
        ...postInfo.postDescription.imageSocial,
        [activeLang.toLowerCase()]: obj.url,
      },
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
    toggleModal();
  };

  const setImageProxy = (modalType === 'social') ? setImageSocial : setImage

  const handleNoIndex = (indexStatus) => {
    setNoIndex(indexStatus);

    const postState = {
      ...postInfo,
      noIndex: indexStatus,
    };
    setPostInfo(postState);
  };

  const handleRequiresCompleteRegistration = () => {
    setRequiresCompleteRegistration(!requiresCompleteRegistration);

    const postState = {
      ...postInfo,
      requiresCompleteRegistration: !requiresCompleteRegistration,
    };

    setPostInfo(postState);
  };

  const storeOptionItemLayout = (opt) => {
    const postDescription = {
      ...postInfo.postDescription,
      itemLayout: opt,
    };
    const postState = {
      ...postInfo,
      postDescription,
    };
    setPostInfo(postState);
  };

  /*
  Head tags this post would ship empty or truncated. Advisory only — saving
  stays available, since most posts predate these checks and an editor fixing
  a typo should not have to write a meta description first.
  */
  const renderSeoWarnings = () => {
    const warnings = [...getPostSeoWarnings(postInfo), ...tourPriceWarnings];
    if (!warnings.length) return null;

    return (
      <ul className="list-none mt-0 pl-0 font-americaMonoRegular text-xs text-orange-light mb-2 mr-4">
        {warnings.map(warning => (
          <li key={warning}>{`⚠ ${warning}`}</li>
        ))}
      </ul>
    );
  };

  const renderComponentButtons = () => {
    const {
      slugName,
    } = postInfo;
    return isEdit ? (
      <button className={`font-americaMonoBold text-xs px-6 py-2 rounded-full ${slugName ? 'text-black' : 'text-grey'} uppercase bg-greyFilter mb-2`} onClick={editPost} type="button" disabled={!slugName}>
        Save changes
      </button>
    ) : (
      <button className={`font-americaMonoBold text-xs px-6 py-2 rounded-full ${slugName ? 'text-black' : 'text-grey'} uppercase bg-greyFilter mb-2`} onClick={addPost} type="button" disabled={!slugName}>
        Save
      </button>
    );
  };

  const renderRoutePostForm = () => {
    const {
      postCategory,
      rolesAllowed,
      slugName,
      postName,
      postDescription: {
        description, title, subtitle = { en: '' }, metaTags, tags, author, url, image, imageSocial, date, expirationDate, cta, itemLayout, fallBackUrl, overlayImage, launcherSortDate, loyaltyPoints,
      },
    } = postInfo;
    return (
      <div className="bg-black text-white px-7 py-1 flex flex-wrap w-full rounded-b-lg">
        <div className="flex w-full">
          {!isEdit ? (
            <label className="font-americaMonoRegular text-xs uppercase my-2 w-1/2 mr-6" htmlFor="slugName">
              Post name
              <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full" onChange={handleSlugNameInput} name="slugName" type="text" value={postName} placeholder="Name of post" />
              <span className="font-americaMonoRegular text-xs p-2 pl-0 block">
                Slugname
                <span className="lowercase ml-2">{slugName}</span>
              </span>
            </label>
          ) : (
            <span className="font-americaMonoRegular text-xs uppercase mt-2 mb-1 w-1/2 mr-4">
              Post name
              <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full" onChange={handleSlugNameInput} name="postName" type="text" value={postName || slugName} placeholder="Name of post" />
              <span className="lowercase ml-2 mt-2px block text-greyFilter">
                Slugname:
                {' '}
                {slugName}
              </span>
            </span>
          )}
          <label className="font-americaMonoRegular text-xs uppercase my-2 w-1/2" htmlFor="url">
            Relink
            <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full" onChange={handlePostDescriptionInput} name="url" type="text" value={url || ''} placeholder="Relink to" />
          </label>
        </div>
        <div className="w-full flex flex-row items-center -mt-3">
          <div className="w-1/3 pr-4">
            <span className="font-americaMonoRegular text-xs block my-2 w-1/2 mr-2">
              Category:
            </span>
            <Select
              className="font-americaMonoRegular text-black font-bold mt-1"
              name="postCategory"
              options={groupedOptions}
              isMulti
              value={transformToSelectData(postCategory)}
              onChange={handleSelect}
              formatGroupLabel={formatGroupLabel}
            />
          </div>
          {postInfo && ((Array.isArray(postInfo.components) && postInfo.components[0].hasOwnProperty('key')) || (!Array.isArray(postInfo.components) && JSON.parse(postInfo.components)[0].hasOwnProperty('key'))) && (
            <>
              <div className="flex w-1/3 items-end pr-4">
                <div className="w-full flex flex-col">
                  <DropDownSelector name="postHeader" title="Select header" action="create" message="No headers available" suggestions={headerKeys} storeOption={handleHeaderChange} margin="mb-8px" value={postInfo.components[0].key} />
                </div>
              </div>
              <div className="flex w-1/3 items-end pr-4">
                <div className="w-full flex flex-col">
                  <DropDownSelector name="postFooter" title="Select Footer" action="create" message="No headers available" suggestions={FOOTER_KEYS} storeOption={handleFooterChange} margin="mb-8px" value={postInfo.components[postInfo.components.length - 1].key} />
                </div>
              </div>
            </>
          )}
          <div className="w-1/3">
            <DateAndTimePickerPost
              wFull
              action="edit"
              title="Post date"
              name="date"
              handleDate={handleDate}
              existingDate={date}
              className="text-center mx-0 xxl:text-left xxl:text-xs h-36px"
              isConfig
            />
          </div>
        </div>
        <div className="w-full flex flex-row items-center -mt-3">
          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs">
              Select related authors
            </label>
            <AsyncSelect
              cacheOptions
              className="font-americaMonoRegular text-black font-bold mt-1"
              loadOptions={loadOptionsForRelatedAuthors}
              defaultOptions
              isMulti
              value={currentRelatedAuthors}
              getOptionLabel={(option) => {
                const { postDescription } = option;
                if (postDescription) {
                  const { title: { en } } = postDescription;
                  return en;
                }
                return 'No name available';
              }}
              getOptionValue={option => option.slugName}
              onChange={handleInputChangeForRelatedAuthors}
            />
          </div>


          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs">
              Select related artists
            </label>
            <AsyncSelect
              cacheOptions
              className="font-americaMonoRegular text-black font-bold mt-1"
              loadOptions={loadOptions}
              defaultOptions
              isMulti
              value={currentRelatedArtists}
              getOptionLabel={(option) => {
                const { postDescription } = option;
                if (postDescription) {
                  const { title: { en } } = postDescription;
                  return en;
                }
                return 'No name available';
              }}
              getOptionValue={option => option.slugName}
              onChange={handleInputChangeForRelatedArtists}
            />
          </div>
          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs">
              Select related magazine
            </label>
            <AsyncSelect
              cacheOptions
              className="font-americaMonoRegular text-black font-bold mt-1"
              loadOptions={loadOptionsForRelatedMagazine}
              defaultOptions
              isClearable
              value={currentRelatedMagazine}
              getOptionLabel={(option) => {
                const { postDescription } = option;
                if (postDescription) {
                  const { title: { en } } = postDescription;
                  return en;
                }
                return 'No name available';
              }}
              getOptionValue={option => option.slugName}
              onChange={handleInputChangeForRelatedMagazine}
            />
          </div>
        </div>
        {!isArtistPage ? null : (
          <div className="w-full flex flex-row items-start mt-4">
            <div className="w-2/3 pr-4">
              <SpotifyArtistsPicker
                value={postInfo.postDescription?.spotifyArtistIds || []}
                onChange={spotifyArtistIds => setPostInfo({
                  ...postInfo,
                  postDescription: { ...postInfo.postDescription, spotifyArtistIds },
                })}
              />
            </div>
          </div>
        )}
        <div className="w-full flex flex-row items-center">
          <div className="w-1/3 pr-4">
            <DateAndTimePickerPost wFull action="edit" title="Date to sort in Launcher" name="launcherSortDate" existingDate={launcherSortDate} handleDate={handleDate} reset={resetDate} resetButtonStyle="absolute -mt-7 mr-10 ml-2 px-4" className="text-center xxl:text-left xxl:text-xs h-36px" isConfig />
          </div>
          <div className="w-1/3 pr-4">
            <DateAndTimePickerPost wFull action="edit" title="Expiration date" name="expirationDate" existingDate={expirationDate} handleDate={handleDate} reset={resetDate} resetButtonStyle="absolute -mt-7 mr-10 ml-2 px-4" className="text-center xxl:text-left xxl:text-xs h-36px" isConfig />
          </div>
          <div className="w-1/3 pr-4">
            <DropDownMenu style="my-6px" menuTitle="itemLayout" text="Select type of launcher item" action="unique" menuItems={itemLayouts} onHandleChange={storeOptionItemLayout} arr={[itemLayout !== '0' ? itemLayout : ''] || []} />
          </div>
          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs" htmlFor="url">
              Select post tags
            </label>
            <AsyncCreatable
              cacheOptions
              className="font-americaMonoRegular text-black font-bold"
              loadOptions={loadTags}
              defaultOptions
              isMulti
              value={transformTags(tags)}
              // getOptionLabel={option => option}
              // getOptionValue={option => option}
              onChange={toggleTag}
              onCreateOption={addNewTag}
            />
          </div>

        </div>
        <div className="w-full flex flex-row -mt-6 mb-6">
          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs">
              Secure page with a role
            </label>
            <AsyncCreatable
              cacheOptions
              className="font-americaMonoRegular text-black font-bold mt-1"
              loadOptions={loadRoles}
              defaultOptions={transformTags(['rdl-subscription', 'rdl-registration', 'pro-online-2021', 'admin'])}
              isMulti
              value={transformTags(rolesAllowed)}
              onChange={toggleRole}
              onCreateOption={addNewRole}
            />
          </div>
          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs">
              Destination url on missing role
            </label>
            <input
              className="font-americaMonoRegular text-black font-bold w-full p-2 rounded mt-1"
              style={{ minHeight: 38 }}
              onChange={handlePostDescriptionInput}
              name="fallBackUrl"
              type="text"
              value={fallBackUrl || ''}
              placeholder="link"
            />
          </div>
          <div className="w-1/3 pr-4">
            <label className="font-americaMonoRegular uppercase text-xs" htmlFor="url">
              Overlay image
            </label>
            <select
              className="font-americaMonoRegular text-black font-bold"
              value={overlayImage}
              onChange={e => handleInputChangeForOverlayImage(e.target.value)}
            >
              <option value={null} />
              <option value="https://assets.rockdelux.com/rdl/assets/premium-bright.png">Premium content - bright</option>
              <option value="https://assets.rockdelux.com/rdl/assets/premium-dark.png">Premium content - dark</option>
            </select>
          </div>
          {
            !isLoyaltyAchievement ? null : (
              <div className="w-1/3 pr-4">
                <label className="font-americaMonoRegular uppercase text-xs">
                  Loyalty Achievement Points
                </label>
                <input
                  className="font-americaMonoRegular text-black font-bold w-full p-2 rounded mt-1"
                  style={{ minHeight: 38 }}
                  onChange={handlePostDescriptionInput}
                  name="loyaltyPoints"
                  type="number"
                  value={loyaltyPoints || ''}
                  placeholder="Points"
                />
              </div>
            )
          }
        </div>
        <div className="flex flex-row w-full xxl:flex-col">
          <LanguageTabsForms description={description} title={title} metaTags={metaTags} activeTabStyle="tab active" width="w-full">
            {(activeLanguage, languages) => (
              <div className="bg-greyWithinForm px-4 xxl1:pr-2 py-1 mb-3 rounded-b-lg mr-1 xxl:mr-0">
                {languages.map(lang => (
                  <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'flex flex-nowrap mx-1 flex-row' : 'hidden'}`}>
                    <div className="w-full xxl1:w-4/5">
                      <label className="font-americaMonoRegular text-xs uppercase my-2 mr-5 w-48%" htmlFor="post title">
                        <span className="flex justify-between items-baseline">
                          Post description
                          <CharCounter value={description[lang]} limits={DESCRIPTION_LIMITS} />
                        </span>
                        <textarea className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-lg h-28" onChange={handlePostDescriptionLangInput} name="description" lang={lang} type="text" value={description[lang] || ''} />
                      </label>
                      <label className="font-americaMonoRegular text-xs uppercase w-48%" htmlFor="title">
                        <span className="flex justify-between items-baseline">
                          Title
                          <CharCounter value={title[lang]} limits={TITLE_LIMITS} />
                        </span>
                        <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full mb-2" onChange={handlePostDescriptionLangInput} name="title" lang={lang} type="text" value={title[lang] || ''} />
                      </label>
                      <label className="font-americaMonoRegular text-xs uppercase w-48%" htmlFor="subtitle">
                        Subtitle
                        <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full mb-2" onChange={handlePostDescriptionLangInput} name="subtitle" lang={lang} type="text" value={`${subtitle && subtitle[lang] ? subtitle[lang] : ''}`} />
                      </label>
                      <label className="font-americaMonoRegular text-xs uppercase w-48%" htmlFor="title">
                        Meta Tags (comma separated)
                        <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full mb-2" onChange={handlePostDescriptionLangInput} name="metaTags" lang={lang} type="text" value={(metaTags && metaTags[lang]) || ''} />
                      </label>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className={`xxl1:w-1/4 px-2 xxl1:ml-2 mt-4 flex items-center flex-col ${image && image[activeLanguage] ? 'justify-evenly' : 'justify-center'}`}>
                        <div className="image">{image && image[activeLanguage] ? !image[activeLanguage].includes('svg') ? <img src={imageModifier(image[activeLanguage], '100x100')} alt={slugName} /> : <img src={image[activeLanguage]} height="100" width="100" alt={slugName} /> : null}</div>
                        <button className="font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase bg-greyFilter mt-4 whitespace-pre" type="button" onClick={() => toggleModal(activeLanguage)}>
                          Select image
                        </button>
                      </div>
                      <div className={`xxl1:w-1/4 px-2 xxl1:ml-2 mt-4 flex items-center flex-col ${ imageSocial?.[activeLanguage] ? 'justify-evenly' : 'justify-center'}`}>
                        {imageSocial?.[activeLanguage] && <div className="image"><img src={imageModifier(imageSocial[activeLanguage], '100x100')} alt={slugName} /></div>}
                        <button className="font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase bg-greyFilter mt-4 whitespace-pre" type="button" onClick={() => toggleModal(activeLanguage, 'social')}>
                          Select Social Media Image
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </LanguageTabsForms>
          {postCategory && (postCategory[0] === 'tours' || postCategory[0] === 'nits-del-forum' || postCategory[0] === 'primavera-labels-tours') && (
          <div className="bg-greyFilterItems rounded-lg px-5 py-1 mb-3 w-full mt-2 ml-1 xxl:ml-0">
            <div className="flex flex-row justify-between">
              <div className={`font-americaMonoRegular text-xs uppercase my-1 text-black flex flex-row items-center font-bold ${(cta.text.en) && 'py-1'}`} htmlFor="ctaButton">
                CTA button
              </div>
              <label className="font-americaMonoRegular text-xs text-black uppercase my-1 py-1 flex flex-row items-center justify-end" htmlFor="ctaButton">
                Inactive
                <div className="mt-1 pb-0 pl-1">
                  <label className="switch mb-0 pb-0 h-6">
                    <input type="checkbox" checked={isCTAButtonInactive} onChange={() => handleCtaIsDisabledInput(!isCTAButtonInactive)} />
                    <div className="switch-slider btn-yn3 switch-round" />
                  </label>
                </div>
              </label>
            </div>
            <div className="flex w-full mb-4 flex-col">
              <LanguageTabsForms description={description} title={title} activeTabStyle="tab active" width="w-full">
                {(activeLanguage, languages) => (
                  <div className="bg-greyWithinForm px-4 pl-4 pr-3 rounded-b-lg">
                    {languages.map(lang => (
                      <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'flex flex-wrap mx-1' : 'hidden'}`}>
                        <div className="w-full mr-1">
                          <label className="font-americaMonoRegular text-xs uppercase" htmlFor="title">
                            Button text
                            <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-full mb-4" onChange={handleCtaLangInput} name="text" lang={lang} type="text" value={cta && cta.text[lang] ? cta.text[lang] : ''} placeholder={placeholderButtonText[lang]} />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </LanguageTabsForms>
              {(!cta.isDisabled) && (
              <label className="font-americaMonoRegular text-xs uppercase w-full flex flex-col justify-end ml-1 text-black -mb-5 mt-3" htmlFor="url">
                URL
                <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 -ml-1 mb-4 rounded-full" onChange={handleCtaUrlInput} name="url" type="text" value={cta.url || ''} placeholder="primaverasound.com/tickets?e=tours" />
              </label>
              )}
            </div>
          </div>
          )}
        </div>
        <div className="flex justify-between items-center w-full">
          {renderSeoWarnings()}
          {renderComponentButtons()}
        </div>
      </div>
    );
  };

  return (
    <>
      {!isLoading ? (
        <div className="post-config">
          {status.status !== 'is-editing' && <Navigator />}
          <div className="bg-greyFilter px-4 flex justify-between items-center rounded-t-lg px-7 h-12">
            <span className="font-americaMonoBold text-sm uppercase text-black">Basic page info</span>
            <label className="switch-no-text ml-4 mr-10 text-black font-americaMonoBold text-sm uppercase flex justify-end w-1/3 items-center" style={height}>
              <span>Requires Complete Registration</span>
              <div className="flex items-center -ml-2">
                <Checkbox
                  checked={postInfo.requiresCompleteRegistration}
                  onChange={handleRequiresCompleteRegistration}
                  color="default"
                  inputProps={{
                    'aria-label': 'checkbox with default color',
                  }}
                />
              </div>
            </label>
            <label className="switch-no-text ml-4 mr-10 text-black font-americaMonoBold text-sm uppercase flex justify-end w-1/3 items-center" style={height}>
              <span>no Index</span>
              <div className="flex items-center -ml-2">
                <Checkbox
                  checked={!!postInfo.noIndex}
                  onChange={() => handleNoIndex(!noIndex)}
                  value="checkedB"
                  color="default"
                  inputProps={{
                    'aria-label': 'checkbox with default color',
                  }}
                />
              </div>
            </label>
            {/* <Notificacion text={status.text} color={status.color} /> */}
          </div>
          {renderRoutePostForm()}
        </div>
      ) : null}
      {isModalOn && (
        <Modal handleClick={toggleModal} positionMiddle>
          <SingleObjUploaderWithSelector selectCallback={setImageProxy} />
        </Modal>
      )}
    </>
  );
};

PostConfig.propTypes = {
  postDescription: PropTypes.shape({
    title: PropTypes.shape({}),
    description: PropTypes.shape({}),
    metaTags: PropTypes.shape({}),
    image: PropTypes.shape({}),
    author: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    active: PropTypes.bool,
    url: PropTypes.string,
    fallBackUrl: PropTypes.string,
  }),
  closeModal: PropTypes.func,
  addStatusMessage: PropTypes.func,
};

PostConfig.defaultProps = {
  postDescription: {
    title: {},
    description: {},
    metaTags: {},
    image: {},
    tags: [],
    url: '',
    author: '',
    fallBackUrl: '',
    active: false,
  },
  closeModal: null,
  addStatusMessage: null,
};

export default PostConfig;
